import { CommonModule } from '@angular/common';
import {
  afterRenderEffect,
  Component,
  computed,
  contentChild,
  ElementRef,
  inject,
  Inject,
  input,
  NgZone,
  OnDestroy,
  output,
  Renderer2,
  Resource,
  ResourceStatus,
  signal,
  TemplateRef,
  viewChild,
  DOCUMENT
} from '@angular/core';
import {
  BackNavigable,
  backNavigation,
  NavigateBackOptions,
} from '@softline/application';
import {
  BreakpointService,
  DEFAULT_MASTER_DETAIL_MASTER_TEMPLATE_CONFIG,
  MasterDetailComponent,
  MasterDetailMasterTemplateConfig,
  SOFTLINE_MASTER_DETAIL_HEIGHT,
  UiCoreModule,
} from '@softline/ui-core';
import { ResourceContainerComponent } from '../../components/resource-container/resource-container.component';
import { MenuOutletComponent } from '../../menu/menu-outlet/menu-outlet.component';
import { PageHeaderComponent } from '../page/page-header/page-header.component';
import { PageComponent } from '../page/page.component';

@Component({
  selector: 'soft-master-detail-page',
  imports: [
    CommonModule,
    PageComponent,
    PageHeaderComponent,
    UiCoreModule,
    MenuOutletComponent,
    ResourceContainerComponent,
  ],
  templateUrl: './master-detail.page.html',
  styleUrl: './master-detail.page.css',
})
export class MasterDetailPage<T> implements OnDestroy, BackNavigable {
  readonly breakpointService = inject(BreakpointService);

  private masterDetail = viewChild<MasterDetailComponent<T>>('masterDetail');
  private masterDetailElem = viewChild('masterDetail', { read: ElementRef });
  private renderer2 = inject(Renderer2);

  private masterDetailHeight = inject(SOFTLINE_MASTER_DETAIL_HEIGHT, { optional: true });

  #observer: ResizeObserver | undefined;

  masterTemplate = input<TemplateRef<any> | null>(null);
  masterHeaderTemplate = input<TemplateRef<any> | null>(null);
  masterBottomTemplate = input<TemplateRef<any> | null>(null);
  masterContainerClass = input<string>();
  masterTemplateConfig = input<MasterDetailMasterTemplateConfig>(
    DEFAULT_MASTER_DETAIL_MASTER_TEMPLATE_CONFIG
  );

  detailTemplate = input<TemplateRef<any> | null>(null);
  detailBottomTemplate = input<TemplateRef<any> | null>(null);
  detailPlaceholder = input<TemplateRef<any> | null>(null);
  masterLoadingTemplate = input<TemplateRef<any> | null>(null);

  loadingEntries = input<number>(5);
  blockScrollY = input<boolean>(false);

  resource = input.required<Resource<T[] | undefined>>();

  readonly #masterDetailOffsetTop = signal(0);

  readonly masterDetailMaxHeightPx = computed(() => {
    return this.document.body.clientHeight - this.#masterDetailOffsetTop();
  });

  selectedChange = output<T | null>();

  header = contentChild(PageHeaderComponent, { descendants: true });

  selected = computed(() => {
    const masterDetail = this.masterDetail();
    if (masterDetail) return masterDetail.selected();
    return null;
  });

  select(index: number): void {
    const masterDetail = this.masterDetail();
    if (masterDetail) masterDetail.select(index);
  }

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private zone: NgZone
  ) {
    backNavigation(this);

    this.#observer = new ResizeObserver((entries) => {
      const offestTop = entries[0].target?.getBoundingClientRect().top;
      this.zone.run(() => {
        console.log('offset: ', offestTop);
        this.#masterDetailOffsetTop.set(offestTop);
      });
    });

    afterRenderEffect(() => {
      const masterDetailElement =
        this.masterDetailElem()?.nativeElement?.firstElementChild;

      if (!masterDetailElement) {
        return;
      }

      this.#observer?.observe(masterDetailElement);
    });

    afterRenderEffect({
      write: () => {
        const height = this.masterDetailMaxHeightPx();
        this.masterDetailHeight?.set(height);
      },
    });
  }

  ngOnDestroy() {
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  async navigateBack(options?: NavigateBackOptions): Promise<void> {
    const isMobile = this.breakpointService.isMobile();
    if ((!this.selected() || !isMobile) && options?.parent)
      await options.parent.navigateBack(options);
    else if (isMobile) this.select(-1);
  }
}
